import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Sparkles, Trophy, Cpu, ShoppingBag, Tv, Zap, TrendingUp, Target, Users, DollarSign, Play } from "lucide-react";
import { useSEO } from "../hooks/useSEO";
import { useJsonLd, breadcrumb } from "../hooks/useJsonLd";

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-100px" }, transition: { duration: 0.6 } };

// Themed Unsplash images per case
const IMG = {
  styx: "https://images.unsplash.com/photo-1531415074968-036ba1b575da?w=1600&q=80&auto=format&fit=crop",
  above40: "https://images.unsplash.com/photo-1556228720-195a672e8a03?w=1600&q=80&auto=format&fit=crop",
  nutralyfe: "https://images.unsplash.com/photo-1584362917165-526a968579e8?w=1600&q=80&auto=format&fit=crop",
  sandbrix: "https://images.unsplash.com/photo-1522869635100-9f4c5e86aa37?w=1600&q=80&auto=format&fit=crop",
  crix: "https://images.unsplash.com/photo-1512428559087-560fa5ceab42?w=1600&q=80&auto=format&fit=crop",
  aiAgent: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=1600&q=80&auto=format&fit=crop",
  ipl: "https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?w=1600&q=80&auto=format&fit=crop",
};

// ---------- Reusable graph primitives ----------
const LineChart = ({ points, color = "#3B82F6", height = 100, id = "lg" }) => {
  const max = Math.max(...points);
  const pts = points.map((v, i) => `${(i / (points.length - 1)) * 400},${height - (v / max) * (height - 10) - 4}`).join(" ");
  return (
    <svg viewBox={`0 0 400 ${height}`} className="w-full">
      <defs>
        <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline points={`0,${height} ${pts} 400,${height}`} fill={`url(#${id})`} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const BarChart = ({ data, color = "#3B82F6" }) => {
  const max = Math.max(...data.map((d) => d.v));
  return (
    <div>
      <div className="flex items-end gap-3 h-40">
        {data.map((d) => (
          <div key={d.l} className="flex-1 h-full flex flex-col justify-end">
            <div
              className="w-full rounded-t-md relative"
              style={{ height: `${(d.v / max) * 100}%`, background: color, opacity: 0.9 }}
            >
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] font-mono opacity-70 whitespace-nowrap" style={{ color }}>{d.v}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-3 mt-2">
        {data.map((d) => (
          <div key={d.l} className="flex-1 text-center text-[10px] font-mono opacity-60 uppercase">{d.l}</div>
        ))}
      </div>
    </div>
  );
};

const FunnelRow = ({ label, value, width, delta, dark = false }) => (
  <div className={`rounded-xl border px-4 py-3 flex items-center justify-between ${dark ? "border-white/10 bg-white/[0.03]" : "border-neutral-200 bg-white"}`}>
    <div className={`text-sm ${dark ? "text-white/80" : "text-neutral-800"}`}>{label}</div>
    <div className="flex items-center gap-3">
      <div className={`h-1.5 rounded-full ${dark ? "bg-white/10" : "bg-neutral-200"}`} style={{ width: 120 }}>
        <div className="h-full rounded-full bg-[#3B82F6]" style={{ width: `${width}%` }} />
      </div>
      <div className={`font-display text-sm ${dark ? "text-white" : "text-neutral-900"} w-16 text-right`}>{value}</div>
      {delta && <div className="text-[10px] font-mono text-[#10B981] w-10">{delta}</div>}
    </div>
  </div>
);

// ================== 05 · STYX SPORTS ==================
const StyxSports = () => (
  <section id="styx" data-testid="case-styx" className="relative bg-[#0F1115] text-white py-28 md:py-36 grain overflow-hidden">
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 relative">
      <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-white/40">07 — OTT Growth</div>
          <div className="mt-6 inline-flex items-center gap-2 text-[#10B981] text-xs font-mono">
            <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] pulse-dot" /> LIVE OTT · CRICKET
          </div>
          <h2 className="mt-4 font-display text-5xl md:text-6xl leading-none tracking-tight">
            Styx <span className="text-white/50">Sports</span>
          </h2>
          <p className="mt-3 text-white/60 text-lg">OTT Growth Marketing</p>

          <div className="mt-6 rounded-2xl overflow-hidden border border-white/10">
            <img src={IMG.styx} alt="Cricket stadium at night" className="w-full h-56 object-cover" />
          </div>

          <p className="mt-6 text-white/70">
            <span className="text-white/40 text-xs font-mono uppercase tracking-[0.15em] block mb-2">Business Goal</span>
            Acquire cricket fans across India, Nepal, Bangladesh &amp; UAE and grow subscriptions during highly competitive cricket seasons.
          </p>

          <div className="mt-6 grid grid-cols-2 gap-3">
            {["India", "Nepal", "Bangladesh", "UAE"].map((c) => (
              <div key={c} className="glass-dark rounded-xl p-3 text-sm text-white/80">🌏 {c}</div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="glass-dark rounded-2xl p-6">
            <div className="text-xs font-mono text-white/40 mb-4">WHAT I DID</div>
            <div className="grid grid-cols-2 gap-3">
              {["GTM Strategy", "Paid Media", "Funnel Optimization", "Audience Segmentation", "Campaign Planning", "Analytics", "Performance Optimization"].map((w) => (
                <div key={w} className="flex items-center gap-2 rounded-lg bg-white/[0.02] border border-white/5 px-3 py-2.5 text-sm">
                  <CheckCircle2 size={14} className="text-[#10B981]" /> {w}
                </div>
              ))}
            </div>
          </div>

          {/* Subscription growth chart */}
          <div className="mt-4 glass-dark rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs font-mono uppercase text-white/40">Subscribers · Monthly</div>
                <div className="mt-1 font-display text-2xl">148K <span className="text-sm text-[#10B981]">↑ +324% vs baseline</span></div>
              </div>
              <div className="flex gap-2 text-[10px] font-mono">
                <span className="px-2 py-0.5 rounded bg-[#3B82F6]/20 text-[#93C5FD]">2024</span>
                <span className="px-2 py-0.5 rounded bg-white/5 text-white/50">2023</span>
              </div>
            </div>
            <LineChart id="styx-line" points={[35, 42, 48, 55, 63, 72, 78, 91, 108, 122, 138, 148]} />
            <div className="flex justify-between mt-2 text-[10px] font-mono text-white/40">
              {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((m) => <span key={m}>{m}</span>)}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-4 gap-3">
            {[
              { v: "4", l: "Countries" },
              { v: "3.2x", l: "Sub Growth" },
              { v: "-38%", l: "CAC" },
              { v: "6.4x", l: "ROAS" },
            ].map((k) => (
              <div key={k.l} className="glass-dark rounded-xl p-4 hover-glow">
                <div className="font-display text-2xl text-white">{k.v}</div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-white/40 mt-1">{k.l}</div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap gap-2 text-[11px] font-mono text-white/60">
            {["GOOGLE ADS", "META", "GA4", "GTM", "APPSFLYER"].map((t) => (
              <span key={t} className="px-2.5 py-1 rounded-md border border-white/10 bg-white/[0.02]">{t}</span>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

// ================== 06 · ABOVE40 ==================
const Above40 = () => (
  <section id="above40" data-testid="case-above40" className="bg-[#F7F7F5] py-28 md:py-36 border-t border-neutral-200">
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
      <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-12 mb-2">
          <div data-testid="above40-sharktank" className="rounded-3xl overflow-hidden border border-[#0B3B1F]/20 bg-gradient-to-br from-[#0B3B1F] via-[#0F4A2A] to-[#0B3B1F] text-white">
            <div className="grid grid-cols-1 md:grid-cols-5">
              <div className="md:col-span-2 relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_marketing-ops-18/artifacts/hp7xcmai_image.png"
                  alt="Above 40 shortlisted for Shark Tank India Season 5"
                  className="w-full h-full object-cover object-center min-h-[280px]"
                  loading="lazy"
                />
              </div>
              <div className="md:col-span-3 p-8 md:p-10 flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 self-start rounded-full bg-[#FFD54A]/15 border border-[#FFD54A]/30 text-[#FFD54A] px-3 py-1 text-[10px] font-mono uppercase tracking-[0.2em]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FFD54A] pulse-dot" /> Breaking · Dec 2025
                </div>
                <h3 className="mt-5 font-display text-3xl md:text-4xl leading-[1.05] tracking-tight">
                  Above 40 Shortlisted for <span className="text-[#FFD54A]">Shark Tank India — Season 5</span>
                </h3>
                <p className="mt-5 text-white/85 text-sm md:text-base leading-relaxed">
                  In just 30 days, Above 40 — India&apos;s first skincare and haircare brand made exclusively for those 40+ — has been shortlisted by Shark Tank India Season 5.
                </p>
                <p className="mt-3 text-white/70 text-sm md:text-base leading-relaxed">
                  We&apos;re rewriting the beauty narrative. No filters. No false promises. Just science-backed solutions that celebrate ageing with strength, style and self-respect. This isn&apos;t about looking younger — it&apos;s about owning your age and glowing with it.
                </p>
                <div className="mt-6 font-display text-lg text-[#FFD54A]">
                  40 isn&apos;t a number. It&apos;s a revolution.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-4">
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-neutral-500">06 — D2C Skincare</div>
          <h2 className="mt-6 font-display text-5xl md:text-6xl tracking-tight text-neutral-900">
            Above40<span className="text-[#10B981]">.</span>
          </h2>
          <p className="mt-3 text-neutral-500">D2C Growth Marketing</p>

          <div className="mt-6 rounded-2xl overflow-hidden border border-neutral-200">
            <img src={IMG.above40} alt="Premium skincare products" className="w-full h-52 object-cover" />
          </div>

          <p className="mt-6 text-neutral-700">
            <span className="text-neutral-400 text-xs font-mono uppercase tracking-[0.15em] block mb-2">Objective</span>
            Scale a premium skincare brand through marketplace and performance marketing.
          </p>

          <div className="mt-6 flex flex-wrap gap-2">
            {["Marketplace Growth", "Performance Ads", "Creative Testing", "Customer Journey", "Offer Optimization", "Retention"].map((s) => (
              <span key={s} className="text-xs px-3 py-1.5 rounded-full border border-neutral-300 bg-white text-neutral-700">{s}</span>
            ))}
          </div>

          <div className="mt-8">
            <div className="text-xs font-mono uppercase tracking-[0.15em] text-neutral-500 mb-3">Platforms</div>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {["Amazon", "Blinkit", "Zepto", "Nykaa", "Flipkart"].map((p) => (
                <div key={p} className="text-xs font-mono text-center py-2 rounded-lg bg-white border border-neutral-200">{p}</div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-8 space-y-4">
          <div className="rounded-2xl bg-white border border-neutral-200 p-6">
            <div className="flex items-center justify-between text-xs font-mono text-neutral-400 mb-6">
              <span className="inline-flex items-center gap-2"><ShoppingBag size={12} /> D2C · FUNNEL SNAPSHOT</span>
              <span className="text-[#10B981]">HEALTHY</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
              {[
                { l: "Traffic", v: "1.2M", w: "100%" },
                { l: "Product Views", v: "480K", w: "40%" },
                { l: "Add To Cart", v: "88K", w: "18%" },
                { l: "Purchase", v: "24K", w: "5%" },
                { l: "Repeat", v: "9.6K", w: "2%" },
              ].map((s, i) => (
                <div key={s.l} className="relative">
                  <div className="text-[10px] uppercase font-mono text-neutral-400">{`0${i + 1}`}</div>
                  <div className="mt-3 rounded-xl border border-neutral-200 bg-neutral-50 p-3 sm:p-4">
                    <div className="font-display text-xl sm:text-2xl text-neutral-900">{s.v}</div>
                    <div className="text-[11px] text-neutral-500 mt-1">{s.l}</div>
                    <div className="mt-3 h-1 bg-neutral-200 rounded overflow-hidden">
                      <div className="h-full bg-[#3B82F6]" style={{ width: s.w }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Marketplace revenue bar chart */}
          <div className="rounded-2xl bg-white border border-neutral-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-xs font-mono uppercase text-neutral-400">Marketplace Revenue Split · Q4</div>
                <div className="mt-1 font-display text-2xl text-neutral-900">₹4.8Cr <span className="text-sm text-[#10B981]">↑ +192% YoY</span></div>
              </div>
              <div className="text-xs font-mono text-neutral-500">All platforms</div>
            </div>
            <BarChart
              color="#3B82F6"
              data={[
                { l: "Amazon", v: 182 },
                { l: "Nykaa", v: 128 },
                { l: "Flipkart", v: 84 },
                { l: "Blinkit", v: 56 },
                { l: "Zepto", v: 38 },
              ]}
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            {[
              { v: "+192%", l: "Marketplace Rev" },
              { v: "3.4x", l: "ROAS" },
              { v: "-27%", l: "CAC" },
            ].map((k) => (
              <div key={k.l} className="rounded-xl bg-[#0F1115] text-white p-4">
                <div className="font-display text-2xl">{k.v}</div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-white/50 mt-1">{k.l}</div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

// ================== 07 · NUTRALYFE ==================
const Nutralyfe = () => (
  <section id="nutralyfe" data-testid="case-nutralyfe" className="bg-white py-28 md:py-36 border-t border-neutral-200">
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
      <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-neutral-500">07 — Native Ads</div>
          <h2 className="mt-6 font-display text-5xl md:text-6xl tracking-tight text-neutral-900">Nutralyfe</h2>
          <p className="mt-3 text-neutral-500">Native Advertising Specialist</p>

          <div className="mt-6 rounded-2xl overflow-hidden border border-neutral-200">
            <img src={IMG.nutralyfe} alt="Wellness supplements" className="w-full h-52 object-cover" />
          </div>

          <p className="mt-6 text-neutral-700 max-w-md">Acquire high-quality users through Native Advertising with disciplined creative testing.</p>

          <div className="mt-8">
            <div className="text-xs font-mono uppercase tracking-[0.15em] text-neutral-500 mb-3">Platforms</div>
            <div className="flex flex-wrap gap-2">
              {["Taboola", "Outbrain", "Google Analytics"].map((p) => (
                <span key={p} className="text-xs font-mono px-3 py-1.5 rounded-md bg-neutral-100 border border-neutral-200 text-neutral-700">{p}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-8 grid gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-neutral-50 border border-neutral-200 p-6">
              <div className="text-xs font-mono uppercase text-neutral-400 mb-4">Strategy Loop</div>
              <ol className="space-y-3">
                {["Audience Research", "Headline Testing", "Creative Testing", "Landing Page", "Optimization", "Scaling"].map((s, i) => (
                  <li key={s} className="flex items-center gap-3">
                    <span className="font-mono text-xs text-[#3B82F6] w-6">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-sm text-neutral-800">{s}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-2xl bg-[#0F1115] text-white p-6">
              <div className="text-xs font-mono uppercase text-white/50 mb-4">Native Funnel</div>
              <div className="space-y-3">
                {[
                  { l: "Headline Impressions", v: "12M", w: 100 },
                  { l: "Click", v: "480K", w: 40 },
                  { l: "Landing Page", v: "312K", w: 26 },
                  { l: "Lead", v: "58K", w: 8 },
                  { l: "Sale", v: "14.2K", w: 3 },
                ].map((r) => (
                  <FunnelRow key={r.l} label={r.l} value={r.v} width={r.w} dark />
                ))}
              </div>
            </div>
          </div>

          {/* CTR headline test bar chart */}
          <div className="rounded-2xl bg-neutral-50 border border-neutral-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-xs font-mono uppercase text-neutral-400">Headline A/B Test · CTR (%)</div>
                <div className="mt-1 font-display text-2xl text-neutral-900">Winner: H4 <span className="text-sm text-[#10B981]">4.8% CTR</span></div>
              </div>
              <div className="text-xs font-mono text-neutral-500">6 variants · 40K imp each</div>
            </div>
            <BarChart
              color="#10B981"
              data={[
                { l: "H1", v: 12 },
                { l: "H2", v: 18 },
                { l: "H3", v: 24 },
                { l: "H4", v: 48 },
                { l: "H5", v: 22 },
                { l: "H6", v: 15 },
              ]}
            />
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

// ================== 08 · SANDBRIX OTT ==================
const Sandbrix = () => (
  <section id="sandbrix" data-testid="case-sandbrix" className="bg-[#EEEDE8] py-28 md:py-36 border-t border-neutral-200">
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
      <motion.div {...fadeUp}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-4">
            <div className="text-xs font-mono tracking-[0.2em] uppercase text-neutral-500">08 — OTT Launch</div>
            <h2 className="mt-6 font-display text-5xl md:text-6xl tracking-tight text-neutral-900">Sandbrix</h2>
            <p className="mt-3 text-neutral-500">OTT Growth Strategy · Sri Lanka</p>

            <div className="mt-6 rounded-2xl overflow-hidden border border-neutral-200">
              <img src={IMG.sandbrix} alt="Streaming platform on TV" className="w-full h-52 object-cover" />
            </div>
          </div>
          <div className="lg:col-span-8">
            <p className="text-neutral-700 max-w-2xl">
              <span className="text-neutral-400 text-xs font-mono uppercase tracking-[0.15em] block mb-2">Challenge</span>
              Launch and grow an OTT platform targeting Sri Lankan audiences.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Audience Strategy", "Social Campaigns", "Performance Marketing", "Content Positioning", "Retention Strategy"].map((s) => (
                <span key={s} className="text-xs px-3 py-1.5 rounded-full border border-neutral-300 bg-white text-neutral-700">{s}</span>
              ))}
            </div>

            {/* Journey timeline */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-3">
              {["Awareness", "App Install", "Watch", "Subscription", "Retention"].map((s, i) => (
                <div key={s} className="relative">
                  <div className="rounded-2xl bg-white border border-neutral-200 p-4">
                    <div className="text-[10px] font-mono text-neutral-400">STAGE {String(i + 1).padStart(2, "0")}</div>
                    <div className="mt-3 flex items-center gap-2">
                      <Tv size={14} className="text-[#3B82F6]" />
                      <div className="font-display text-base text-neutral-900">{s}</div>
                    </div>
                  </div>
                  {i < 4 && <div className="hidden md:block absolute top-1/2 -right-2 -translate-y-1/2 text-neutral-400"><ArrowRight size={14} /></div>}
                </div>
              ))}
            </div>

            {/* Install growth line */}
            <div className="mt-6 rounded-2xl bg-white border border-neutral-200 p-6">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs font-mono uppercase text-neutral-400">Weekly App Installs</div>
                  <div className="mt-1 font-display text-2xl text-neutral-900">84K <span className="text-sm text-[#10B981]">↑ 5x MoM</span></div>
                </div>
                <div className="text-xs font-mono text-neutral-500">Launch → Week 12</div>
              </div>
              <LineChart id="sandbrix-line" color="#3B82F6" points={[6, 8, 12, 15, 22, 30, 38, 47, 55, 66, 74, 84]} />
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

// ================== 09 · CRIX ==================
const Crix = () => (
  <section id="crix" data-testid="case-crix" className="bg-white py-28 md:py-36 border-t border-neutral-200">
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
      <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-neutral-500">10 — AI Product</div>
          <h2 className="mt-6 font-display text-5xl sm:text-6xl md:text-7xl tracking-tight text-neutral-900">CRIX</h2>
          <p className="mt-3 text-neutral-500">AI Powered Cricket Analytics Platform</p>

          <div className="mt-6 rounded-2xl overflow-hidden border border-neutral-200">
            <img src={IMG.crix} alt="Mobile analytics app" className="w-full h-56 object-cover" />
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {["Growth Strategy", "Product Marketing", "User Acquisition"].map((r) => (
              <span key={r} className="text-xs px-3 py-1.5 rounded-full bg-neutral-100 border border-neutral-200 text-neutral-700">{r}</span>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7 space-y-4">
          <div className="rounded-2xl border border-neutral-200 p-6 bg-neutral-50">
            <div className="text-xs font-mono uppercase text-neutral-400 mb-4">Features Shipped</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {["AI Predictions", "Analytics", "Referral System", "ASO", "Launch Strategy"].map((f) => (
                <div key={f} className="rounded-xl bg-white border border-neutral-200 p-4 hover:border-[#3B82F6] transition">
                  <Sparkles size={14} className="text-[#3B82F6]" />
                  <div className="mt-3 font-display text-sm text-neutral-900">{f}</div>
                </div>
              ))}
            </div>
          </div>

          {/* D1/D7/D30 retention chart */}
          <div className="rounded-2xl border border-neutral-200 bg-white p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs font-mono uppercase text-neutral-400">Retention Cohort</div>
                <div className="mt-1 font-display text-2xl text-neutral-900">D30: 42% <span className="text-sm text-[#10B981]">+18pp vs industry</span></div>
              </div>
              <div className="text-[10px] font-mono text-neutral-500">Cohort: 12K users</div>
            </div>
            <BarChart
              color="#3B82F6"
              data={[
                { l: "D1", v: 88 },
                { l: "D3", v: 74 },
                { l: "D7", v: 62 },
                { l: "D14", v: 52 },
                { l: "D30", v: 42 },
              ]}
            />
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {["Install", "Onboarding", "Prediction", "Daily Engagement", "Referral"].map((s, i) => (
              <div key={s} className="flex items-center gap-2 flex-shrink-0">
                <span className="text-xs px-3 py-2 rounded-full bg-[#0F1115] text-white">{s}</span>
                {i < 4 && <ArrowRight size={12} className="text-neutral-400" />}
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

// ================== 10 · AI MARKETING AGENT ==================
const AIAgent = () => (
  <section id="ai-agent" data-testid="case-ai-agent" className="relative bg-[#0F1115] text-white py-28 md:py-36 grain overflow-hidden">
    <div className="absolute inset-0 mesh-bg opacity-70" />
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 relative">
      <motion.div {...fadeUp}>
        <div className="text-xs font-mono tracking-[0.2em] uppercase text-white/50">11 — Signature Project</div>
        <div className="mt-6 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-[#3B82F6]">
              <Cpu size={12} /> Autonomous Marketing Intelligence
            </div>
            <h2 className="mt-4 font-display text-5xl md:text-7xl leading-[0.95] tracking-tight">
              AI Marketing<br /><span className="text-[#3B82F6]">Agent.</span>
            </h2>
            <p className="mt-6 text-white/70 text-lg max-w-xl">
              An autonomous agent that ingests ad and analytics data, detects issues, recommends budget shifts, generates creative ideas and emails a decision-ready daily report.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-2xl overflow-hidden border border-white/10">
              <img src={IMG.aiAgent} alt="AI marketing dashboard" className="w-full h-64 object-cover" />
            </div>
          </div>
        </div>

        {/* Pipeline */}
        <div className="mt-14 rounded-2xl glass-dark p-6">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/40 mb-6">Pipeline</div>
          <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-8 gap-2">
            {["Google Ads", "Meta Ads", "GA4", "AI Agent", "Detect Issues", "Recommend Budget", "Generate Creative", "Email Report"].map((s, i) => (
              <div key={s} className="relative">
                <div className={`rounded-xl border p-4 h-full ${i === 3 ? "border-[#3B82F6] bg-[#3B82F6]/10" : "border-white/10 bg-white/[0.02]"}`}>
                  <div className="text-[10px] font-mono text-white/40">NODE {String(i + 1).padStart(2, "0")}</div>
                  <div className="mt-6 font-display text-sm">{s}</div>
                </div>
                {i < 7 && <ArrowRight size={12} className="hidden lg:block absolute top-1/2 -right-2.5 -translate-y-1/2 text-white/30" />}
              </div>
            ))}
          </div>
        </div>

        {/* Impact graph — budget reallocation */}
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <div className="glass-dark rounded-2xl p-6">
            <div className="text-xs font-mono uppercase text-white/40 mb-4">Autonomous Budget Reallocation (7d)</div>
            <LineChart id="agent-line" color="#10B981" points={[100, 96, 108, 112, 128, 142, 156, 174, 188, 202, 218, 236]} />
            <div className="mt-3 flex items-center justify-between text-xs text-white/60">
              <span>ROAS index</span>
              <span className="text-[#10B981]">+136% since Day 1</span>
            </div>
          </div>
          <div className="glass-dark rounded-2xl p-6">
            <div className="text-xs font-mono uppercase text-white/40 mb-4">Decisions Per Day</div>
            <BarChart
              color="#3B82F6"
              data={[
                { l: "Mon", v: 24 },
                { l: "Tue", v: 32 },
                { l: "Wed", v: 41 },
                { l: "Thu", v: 38 },
                { l: "Fri", v: 52 },
                { l: "Sat", v: 44 },
                { l: "Sun", v: 36 },
              ]}
            />
            <div className="mt-3 text-xs text-white/60">Avg 38 autonomous actions / day · 0 manual overrides</div>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-[#3B82F6]/40 bg-[#3B82F6]/10 p-6 flex items-center gap-4">
          <Zap size={20} className="text-[#3B82F6]" />
          <div>
            <div className="font-display text-xl">Autonomous Marketing Intelligence for Faster Decision Making.</div>
            <div className="text-sm text-white/60 mt-1">From data → insight → action, in one loop.</div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

// ================== 11 · IPL 2025 ==================
const IPL = () => (
  <section id="ipl" data-testid="case-ipl" className="bg-white py-28 md:py-36 border-t border-neutral-200">
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
      <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-neutral-500">05 — Featured Project</div>
          <h2 className="mt-6 font-display text-5xl md:text-6xl tracking-tight text-neutral-900 flex items-center gap-3">
            <Trophy className="text-[#F59E0B]" /> IPL 2025
          </h2>
          <p className="mt-3 text-neutral-500">Campaign Execution at Scale · IPL · SA20 · BBL</p>

          <div className="mt-6 rounded-2xl overflow-hidden border border-neutral-200">
            <img src={IMG.ipl} alt="Cricket stadium under floodlights" className="w-full h-52 object-cover" />
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {["IPL 2025", "SA20", "BBL"].map((t) => (
              <span key={t} className="text-[11px] font-mono px-2.5 py-1 rounded-md bg-white border border-neutral-200 text-neutral-700">{t}</span>
            ))}
          </div>

          <div className="mt-8 space-y-3">
            {["Real-time decision making", "High budget optimization", "Cross-functional coordination"].map((h) => (
              <div key={h} className="flex items-center gap-2 text-neutral-800"><CheckCircle2 size={14} className="text-[#10B981]" /> {h}</div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-8 space-y-4">
          <div className="rounded-2xl bg-neutral-50 border border-neutral-200 p-6">
            <div className="text-xs font-mono uppercase text-neutral-400 mb-6">Timeline</div>
            <div className="relative pl-6">
              <div className="absolute left-2 top-0 bottom-0 w-px bg-neutral-300" />
              {["Planning", "Creative", "Campaign Launch", "Live Optimization", "Reporting", "Scaling"].map((s, i) => (
                <div key={s} className="relative mb-5 last:mb-0">
                  <div className="absolute -left-[18px] top-2 w-3 h-3 rounded-full bg-[#3B82F6] ring-4 ring-white" />
                  <div className="rounded-xl bg-white border border-neutral-200 p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs text-neutral-400">T{String(i + 1).padStart(2, "0")}</span>
                      <span className="font-display text-lg text-neutral-900">{s}</span>
                    </div>
                    <ArrowRight size={14} className="text-neutral-400" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Match-day spend vs conversions bar */}
          <div className="rounded-2xl bg-white border border-neutral-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-xs font-mono uppercase text-neutral-400">Match-Day Spend vs Conversions</div>
                <div className="mt-1 font-display text-2xl text-neutral-900">74 matches <span className="text-sm text-[#10B981]">Peak ROAS 8.4x</span></div>
              </div>
              <div className="flex gap-3 text-[10px] font-mono">
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#3B82F6]" /> Spend</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#10B981]" /> Conversions</span>
              </div>
            </div>
            <BarChart
              color="#3B82F6"
              data={[
                { l: "W1", v: 42 },
                { l: "W2", v: 58 },
                { l: "W3", v: 76 },
                { l: "W4", v: 68 },
                { l: "W5", v: 92 },
                { l: "W6", v: 88 },
                { l: "W7", v: 104 },
                { l: "W8", v: 118 },
              ]}
            />
          </div>

          <div className="grid grid-cols-4 gap-3">
            {[
              { v: "8.4x", l: "Peak ROAS" },
              { v: "74", l: "Matches" },
              { v: "24/7", l: "War Room" },
              { v: "-41%", l: "CPA" },
            ].map((k) => (
              <div key={k.l} className="rounded-xl bg-[#0F1115] text-white p-4">
                <div className="font-display text-2xl">{k.v}</div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-white/50 mt-1">{k.l}</div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

export default function Work() {
  useSEO({
    title: "Case Studies · Styx, Above40, Nutralyfe, CRIX, IPL 2025 · Shivani Ranawat",
    description: "Seven growth case studies across OTT, D2C skincare, native advertising, AI product marketing and cricket-scale (IPL, SA20, BBL) campaign execution — the systems behind 50+ brands and 5–7x ROAS.",
    path: "/work",
  });
  useJsonLd([
    breadcrumb([{ name: "Home", path: "/" }, { name: "Case Studies", path: "/work" }]),
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Case Studies · Shivani Ranawat",
      about: [
        "IPL 2025", "Above40", "Styx Sports", "Nutralyfe", "Sandbrix", "CRIX", "AI Marketing Agent",
      ],
      creator: { "@id": "https://marketing-ops-18.emergent.host/#shivani" },
    },
  ]);
  const jumps = [
    { l: "IPL 2025", h: "#ipl" },
    { l: "Above40", h: "#above40" },
    { l: "Styx Sports", h: "#styx" },
    { l: "Nutralyfe", h: "#nutralyfe" },
    { l: "Sandbrix", h: "#sandbrix" },
    { l: "CRIX", h: "#crix" },
    { l: "AI Agent", h: "#ai-agent" },
  ];
  return (
    <div data-testid="work-page">
      <section className="pt-32 pb-16 bg-[#F7F7F5] border-b border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-neutral-500">Case Studies · 2019 → 2026</div>
          <h1 className="mt-6 font-display text-5xl md:text-7xl tracking-tight text-neutral-900 max-w-4xl leading-[0.98]">
            Seven systems.<br /> Five markets. <span className="text-neutral-400">One playbook.</span>
          </h1>
          <div className="mt-8 flex flex-wrap gap-2 text-xs font-mono">
            {jumps.map((c) => (
              <a
                key={c.l}
                href={c.h}
                data-testid={`work-jump-${c.l.replace(/\s/g, "-").toLowerCase()}`}
                className="px-3 py-1.5 rounded-full border border-neutral-300 hover:border-[#3B82F6] hover:text-[#3B82F6] text-neutral-700 transition"
              >
                {c.l}
              </a>
            ))}
          </div>

          {/* Case study narrative framework */}
          <div data-testid="case-framework-strip" className="mt-12 rounded-2xl border border-neutral-200 bg-white p-5 md:p-6">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500 mb-4">Every case study follows this structure</div>
            <div className="flex flex-wrap items-center gap-2">
              {["Challenge", "Research", "Strategy", "Execution", "Results", "Lessons Learned"].map((s, i, arr) => (
                <span key={s} className="inline-flex items-center gap-2">
                  <span className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-xs text-neutral-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6]" />
                    {s}
                  </span>
                  {i < arr.length - 1 && <ArrowRight size={12} className="text-neutral-400" />}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <IPL />
      <Above40 />
      <StyxSports />
      <Nutralyfe />
      <Sandbrix />
      <Crix />
      <AIAgent />
    </div>
  );
}
